import { useState, useEffect, useCallback } from 'react';
import {
  Save, Image as ImageIcon, Loader2, Eye, Sparkles, ToggleLeft, AlertCircle,
} from 'lucide-react';
import AdminLayout from '../../components/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from '../../components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import CloudinaryUpload from '../../components/CloudinaryUpload';
import api from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

const DEFAULT_HERO = {
  enabled: true,
  image_url: '',
  title: 'Kits TOP de Alimentos',
  subtitle: 'Kits de alimentos para família',
  cta_text: '',
  cta_link: '',
  show_search: true,
  bg_style: 'gradient',
  bg_color: '',
};

const AdminHomeBanner = () => {
  const { user, loading: authLoading } = useAuth();
  const [hero, setHero] = useState(DEFAULT_HERO);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  // Quando authLoading=true OU user=null, ainda não sabemos se é owner.
  // Só travamos a UI quando confirmadamente NÃO é owner.
  const isOwner = !!user?.is_owner;
  const ownerKnown = !authLoading && !!user;
  const blockEdit = ownerKnown && !isOwner;

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/settings');
        setHero({ ...DEFAULT_HERO, ...(data?.hero || {}) });
      } catch (e) {
        toast.error('Erro ao carregar banner');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const update = (patch) => {
    setHero((prev) => ({ ...prev, ...patch }));
    setDirty(true);
  };

  const notifySettingsUpdated = () => {
    window.dispatchEvent(new Event('settings:updated'));
    try {
      localStorage.setItem('kitstop:settings:updated', String(Date.now()));
    } catch {
      // ignore
    }
  };

  // saveHero pode receber um override (ex: imagem recém-uploaded) para evitar race condition
  const saveHero = useCallback(async (override = null) => {
    if (blockEdit) {
      toast.error('Apenas o proprietário pode salvar o banner');
      return false;
    }
    const payload = override ? { ...hero, ...override } : hero;
    if (!payload.title?.trim()) {
      toast.error('O título é obrigatório');
      return false;
    }
    setSaving(true);
    try {
      await api.put('/admin/settings/hero', payload);
      notifySettingsUpdated();
      setDirty(false);
      return true;
    } catch (e) {
      const msg = e.response?.data?.detail || 'Erro ao salvar';
      toast.error(typeof msg === 'string' ? msg : JSON.stringify(msg));
      return false;
    } finally {
      setSaving(false);
    }
  }, [hero, blockEdit]);

  const handleSaveClick = async () => {
    const ok = await saveHero();
    if (ok) toast.success('Banner atualizado! Mudanças refletem na página inicial.');
  };

  // Quando a imagem é enviada, salva AUTOMATICAMENTE para evitar "subi mas não persistiu"
  const handleImageUpload = async (url) => {
    setHero((prev) => ({ ...prev, image_url: url }));
    setDirty(true);
    const ok = await saveHero({ image_url: url });
    if (ok) toast.success('Nova imagem do banner salva e publicada!');
  };

  const handleImageRemove = async () => {
    setHero((prev) => ({ ...prev, image_url: '' }));
    setDirty(true);
    const ok = await saveHero({ image_url: '' });
    if (ok) toast.success('Imagem removida do banner.');
  };

  if (loading || authLoading) {
    return (
      <AdminLayout title="Página Inicial — Banner">
        <div className="py-16 text-center">
          <Loader2 className="w-10 h-10 mx-auto animate-spin text-slate-400" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Página Inicial — Banner Principal">
      {blockEdit && (
        <div className="mb-6 p-4 rounded-xl bg-amber-50 border border-amber-200 text-sm text-amber-900 flex items-start gap-2" data-testid="non-owner-warning">
          <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
          <div>
            <strong>Modo leitura:</strong> apenas o proprietário pode editar o banner. Peça ao Rubens (problicidadenova@gmail.com) para conceder acesso.
          </div>
        </div>
      )}

      {dirty && !blockEdit && (
        <div className="mb-4 p-3 rounded-xl bg-blue-50 border border-blue-200 text-sm text-blue-900 flex items-center justify-between gap-2" data-testid="dirty-warning">
          <span className="flex items-center gap-2">
            <AlertCircle className="w-4 h-4" />
            Você tem alterações não salvas
          </span>
          <Button size="sm" onClick={handleSaveClick} disabled={saving} data-testid="hero-save-inline">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Salvar agora'}
          </Button>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Edição */}
        <Card className="rounded-2xl">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <Sparkles className="w-5 h-5" />
              Conteúdo do Banner
            </CardTitle>
            <CardDescription>
              Estas informações aparecem no topo da página inicial do site.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            {/* Ativo/Inativo */}
            <div className="flex items-center justify-between rounded-xl border p-3">
              <div>
                <Label className="text-sm flex items-center gap-1">
                  <ToggleLeft className="w-4 h-4" /> Banner ativo
                </Label>
                <p className="text-xs text-muted-foreground">
                  Se desligado, o banner fica oculto e a homepage começa direto pelos produtos
                </p>
              </div>
              <Switch
                checked={hero.enabled}
                onCheckedChange={(v) => update({ enabled: v })}
                disabled={blockEdit}
                data-testid="hero-enabled-switch"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="title">Título principal *</Label>
              <Input
                id="title"
                value={hero.title}
                onChange={(e) => update({ title: e.target.value })}
                placeholder="Kits TOP de Alimentos"
                disabled={blockEdit}
                data-testid="hero-title-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="subtitle">Subtítulo / frase de apresentação</Label>
              <Input
                id="subtitle"
                value={hero.subtitle}
                onChange={(e) => update({ subtitle: e.target.value })}
                placeholder="Kits de alimentos para família"
                disabled={blockEdit}
                data-testid="hero-subtitle-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-1">
                <ImageIcon className="w-4 h-4" /> Imagem principal
              </Label>
              <CloudinaryUpload
                folder="hero-banner"
                currentUrl={hero.image_url}
                onUpload={handleImageUpload}
                onRemove={handleImageRemove}
              />
              <p className="text-xs text-muted-foreground">
                💡 A imagem é salva e publicada automaticamente assim que o upload conclui.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="cta_text">Texto do botão (opcional)</Label>
                <Input
                  id="cta_text"
                  value={hero.cta_text || ''}
                  onChange={(e) => update({ cta_text: e.target.value })}
                  placeholder="Ex: Ver Catálogo"
                  disabled={blockEdit}
                  data-testid="hero-cta-text"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cta_link">Link do botão</Label>
                <Input
                  id="cta_link"
                  value={hero.cta_link || ''}
                  onChange={(e) => update({ cta_link: e.target.value })}
                  placeholder="Ex: #produtos ou https://..."
                  disabled={blockEdit}
                  data-testid="hero-cta-link"
                />
              </div>
            </div>

            <div className="flex items-center justify-between rounded-xl border p-3">
              <div>
                <Label className="text-sm">Mostrar campo de busca</Label>
                <p className="text-xs text-muted-foreground">
                  Exibe o campo de busca dentro do banner
                </p>
              </div>
              <Switch
                checked={hero.show_search}
                onCheckedChange={(v) => update({ show_search: v })}
                disabled={blockEdit}
                data-testid="hero-show-search-switch"
              />
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Estilo do fundo</Label>
                <Select
                  value={hero.bg_style}
                  onValueChange={(v) => update({ bg_style: v })}
                  disabled={blockEdit}
                >
                  <SelectTrigger data-testid="hero-bg-style"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="gradient">Gradiente (padrão da marca)</SelectItem>
                    <SelectItem value="solid">Cor sólida</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {hero.bg_style === 'solid' && (
                <div className="space-y-2">
                  <Label>Cor de fundo</Label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={hero.bg_color || '#1e40af'}
                      onChange={(e) => update({ bg_color: e.target.value })}
                      disabled={blockEdit}
                      className="h-10 w-14 rounded border"
                      data-testid="hero-bg-color"
                    />
                    <Input
                      value={hero.bg_color || ''}
                      onChange={(e) => update({ bg_color: e.target.value })}
                      placeholder="#1e40af"
                      disabled={blockEdit}
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button
                onClick={handleSaveClick}
                disabled={saving || blockEdit}
                data-testid="hero-save-button"
                className="rounded-xl"
              >
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                {saving ? 'Salvando...' : 'Salvar Banner'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Preview */}
        <Card className="rounded-2xl overflow-hidden lg:sticky lg:top-20 h-fit">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <Eye className="w-5 h-5" /> Pré-visualização
            </CardTitle>
            <CardDescription>Como o banner aparece na home</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            {hero.enabled ? (
              <div
                className={hero.bg_style === 'solid' ? 'text-white p-6' : 'gradient-hero text-white p-6'}
                style={hero.bg_style === 'solid' && hero.bg_color ? { backgroundColor: hero.bg_color } : undefined}
                data-testid="hero-preview"
              >
                <div className="flex flex-col md:flex-row items-center gap-4">
                  {hero.image_url && (
                    <img
                      src={hero.image_url}
                      alt={hero.title}
                      className="w-32 md:w-40 rounded-xl shadow-xl flex-shrink-0"
                    />
                  )}
                  <div className="flex-1 text-center md:text-left">
                    <h2 className="font-heading text-2xl md:text-3xl font-extrabold mb-2">
                      {hero.title || '(sem título)'}
                    </h2>
                    {hero.subtitle && (
                      <p className="text-sm md:text-base text-white/90 mb-3">
                        {hero.subtitle}
                      </p>
                    )}
                    {hero.cta_text && hero.cta_link && (
                      <span className="inline-block mb-3 px-4 py-1.5 rounded-lg bg-white/20 text-sm font-semibold">
                        {hero.cta_text}
                      </span>
                    )}
                    {hero.show_search && (
                      <div className="flex gap-1.5 max-w-xs mx-auto md:mx-0">
                        <input
                          readOnly
                          placeholder="Buscar..."
                          className="flex-1 h-9 rounded-lg px-3 text-xs bg-white text-slate-700"
                        />
                        <button type="button" className="h-9 px-3 rounded-lg bg-yellow-400 text-slate-800 text-xs font-semibold">
                          Buscar
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="p-10 text-center text-slate-400 bg-slate-50">
                Banner desativado — a homepage começará direto pelos produtos
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminHomeBanner;
